import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, CheckCircle, Calendar, XCircle, RefreshCw } from 'lucide-react';
import { useCallMetricsRPC } from '@/hooks/useCallMetricsRPC';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ActiveCallsWidget from './ActiveCallsWidget';
import CallsListTable from './CallsListTable';
import { UserCallsDetailModal } from './UserCallsDetailModal';

export default function CallsMetrics({ dateRange, hideActiveCallsWidget = false }) {
  const { t } = useTranslation();
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Use RPC-based hook instead of pagination-based hook
  const { data: metrics, isLoading: loading, error, refetch } = useCallMetricsRPC({
    dateRange,
    userIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
    direction: selectedDirection,
  });

  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    refetch();
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const { data, error } = await supabase
        .from('app_users')
        .select('id, display_name, first_name, last_name, active')
        .eq('active', true)
        .order('first_name');

      if (error) throw error;

      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const errorMessage = error?.message || String(error);

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-destructive">
          {t('calls.metrics.errorLoading')}: {errorMessage}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Calls Widget */}
      {!hideActiveCallsWidget && <ActiveCallsWidget />}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2">
              <Button
                variant={selectedDirection === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDirection(null)}
              >
                {t('calls.metrics.allCalls')}
              </Button>
              <Button
                variant={selectedDirection === 'inbound' ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDirection('inbound')}
              >
                <PhoneIncoming className="h-4 w-4 mr-2" />
                {t('calls.inbound')}
              </Button>
              <Button
                variant={selectedDirection === 'outbound' ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDirection('outbound')}
              >
                <PhoneOutgoing className="h-4 w-4 mr-2" />
                {t('calls.outbound')}
              </Button>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Select
                value={selectedUserIds[0] || 'all'}
                onValueChange={(value) => {
                  if (value === 'all') {
                    setSelectedUserIds([]);
                  } else {
                    setSelectedUserIds([value]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('calls.metrics.allUsers')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('calls.metrics.allUsers')}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name || `${user.first_name} ${user.last_name}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? t('calls.metrics.updating') : t('calls.metrics.update')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.metrics.totalCalls')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2">{metrics?.totalCalls || 0}</p>
              )}
            </div>
            <Phone className="h-8 w-8 text-primary" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.inbound')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-blue-600">{metrics?.inboundCalls || 0}</p>
              )}
            </div>
            <PhoneIncoming className="h-8 w-8 text-blue-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.outbound')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-green-600">{metrics?.outboundCalls || 0}</p>
              )}
            </div>
            <PhoneOutgoing className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.metrics.avgDuration')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2">{formatDuration(metrics?.averageDuration || 0)}</p>
              )}
            </div>
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.completed')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-green-600">{metrics?.completedCalls || 0}</p>
              )}
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.scheduled')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-blue-600">{metrics?.scheduledCalls || 0}</p>
              )}
            </div>
            <Calendar className="h-8 w-8 text-blue-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.missed')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2 text-red-600">{metrics?.missedCalls || 0}</p>
              )}
            </div>
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('calls.metrics.totalDuration')}</p>
              {loading ? (
                <Skeleton className="h-8 w-16 mt-2" />
              ) : (
                <p className="text-3xl font-bold mt-2">{formatDuration(metrics?.totalDuration || 0)}</p>
              )}
            </div>
            <Clock className="h-8 w-8 text-purple-600" />
          </div>
        </Card>
      </div>

      {/* Chart - Calls by Date */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{t('calls.metrics.callsByDate')}</h3>
        {loading ? (
          <Skeleton className="h-[300px] w-full" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metrics?.callsByDate || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="inbound" fill="#2563eb" name={t('calls.inbound')} />
              <Bar dataKey="outbound" fill="#16a34a" name={t('calls.outbound')} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Table - Calls by User */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{t('calls.metrics.callsByUser')}</h3>
        {loading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('calls.metrics.user')}</TableHead>
                  <TableHead className="text-right">{t('calls.metrics.total')}</TableHead>
                  <TableHead className="text-right">{t('calls.inbound')}</TableHead>
                  <TableHead className="text-right">{t('calls.outbound')}</TableHead>
                  <TableHead className="text-right">{t('calls.completed')}</TableHead>
                  <TableHead className="text-right">{t('calls.metrics.avgDuration')}</TableHead>
                  <TableHead className="text-right">Conexões</TableHead>
                  <TableHead className="text-right">Taxa Atend.</TableHead>
                  <TableHead className="text-right">Dur. Média Conex.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics?.callsByUser && metrics.callsByUser.length > 0 ? (
                  metrics.callsByUser.map((user) => (
                    <TableRow 
                      key={user.userId}
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => {
                        setSelectedUser({ id: user.userId, name: user.userName });
                        setDetailModalOpen(true);
                      }}
                    >
                      <TableCell className="font-medium">{user.userName}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{user.totalCalls}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-blue-600">{user.inbound}</TableCell>
                      <TableCell className="text-right text-green-600">{user.outbound}</TableCell>
                      <TableCell className="text-right text-green-600">{user.completed}</TableCell>
                      <TableCell className="text-right">{formatDuration(user.averageDuration)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          {user.successfulCalls}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={user.answerRate >= 30 ? 'text-emerald-600 font-medium' : 'text-amber-600'}>
                          {user.answerRate.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{formatDuration(user.avgSuccessfulDuration)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      {t('calls.metrics.noDataAvailable')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Lista de Chamadas com Filtros Avançados */}
      <CallsListTable 
        dateRange={dateRange}
        selectedUserIds={selectedUserIds}
        direction={selectedDirection}
      />

      {/* Modal de detalhes do usuário */}
      {selectedUser && (
        <UserCallsDetailModal
          open={detailModalOpen}
          onOpenChange={setDetailModalOpen}
          userName={selectedUser.name}
          userId={selectedUser.id}
          dateRange={dateRange}
          direction={selectedDirection}
        />
      )}
    </div>
  );
}
